import RBCFilterStore, {
    IRBCFilter,
    initRBCFilterItems,
} from "components/Conversations/Views/Drawer/components/DeepFilter/RBCFilterStore";
import OutlineFilterStore, {
    IOutlineFilter,
    initOutlineFilterItems,
} from "components/Conversations/Views/Drawer/components/DeepFilter/OutlineFilterStore";
import WordsPhrasesStore, {
    IWordsPhrasesModel,
    initWordsPhrasesItems,
} from "components/Conversations/Views/Drawer/components/DeepFilter/WordsPhrasesStore";
import _ from "lodash";
import { action, computed, makeObservable, observable, runInAction, toJS } from "mobx";
import moment from "moment";
import { BaseStore } from "stores/BaseStore";
import { HierarchyService } from "services/HierarchyService";
import {
    DatePickerComponentStore,
    DateReferenceOption,
    getDateFromDateRangeLabel,
} from "stores/ComponentStores/DatePickerComponentStore";
import type { DateRangeLabel } from "stores/ComponentStores/DatePickerComponentStore";
import { IRootStore } from "stores/RootStore";
import { toIsoByDateReference } from "utils/DateTimeUtils";
import type {
    ApplicationFilters,
    ContactTypeOption,
    PatientJourneyStageOption,
    DataLabel,
    ModuleScore,
    QuestionResultFilter,
    PrimaryDriver,
    PrimaryTopic,
    SimpleTheme,
    SimpleTopicCluster,
    LanguageFilterObject,
    ExtendedMetadataGrouped,
} from "./ApplicationFiltersStore";
import { CallDirection } from "./Filters/CallDirection";
import { EvaluationType } from "./Filters/EvaluationType";
import { HipaaCompliance } from "./Filters/HipaaCompliance";
import { MediaType } from "./Filters/MediaType";
import { Sentiment } from "./Filters/Sentiment";
import { PatientJourneyStage } from "./Filters/PatientJourneyStage";
import { Language } from "./Filters/Language";
import type { SlimLLMClassifierResponse } from "models/LLMs";
import { SafetyEventTypeOperator, type SafetyEvent } from "./Filters/SafetyEvent";
import {
    OBJECT_FILTER_KEYS,
    type ObjectFilterKey,
    compareFiltersByKey as compareObjectFiltersByKey,
    type FilterComparisonContext,
    type FiltersComparisonResult,
} from "components/Signals/BaseObjects/ObjectFilters";

const normalizeExtendedMetadataGroupedValues = (
    values: string[],
): string[] =>
    values
        .filter((entry): entry is string => typeof entry === "string")
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0);

const sanitizeExtendedMetadataGrouped = (
    extendedMetadataGrouped?: Record<string, ExtendedMetadataGrouped>,
) => {
    if (!extendedMetadataGrouped) return undefined;

    const sanitizedGrouped: Record<string, ExtendedMetadataGrouped> = {};

    Object.entries(extendedMetadataGrouped).forEach(([key, value]) => {
        const normalizedValues = normalizeExtendedMetadataGroupedValues(
            value.values,
        );
        if (!normalizedValues.length) return;

        sanitizedGrouped[key] = {
            contains: value.contains,
            values: normalizedValues,
        };
    });

    return Object.keys(sanitizedGrouped).length > 0
        ? sanitizedGrouped
        : undefined;
};

export class ApplicationFiltersInstanceStore
    extends BaseStore
    implements ApplicationFilters
{
    public static Tasks = {
        VALIDATE_WORDS_AND_PHRASES: "Validate Words and Phrases",
        LOAD_AGENTS: "Load Agents",
        LOAD_HIERARCHY_LABELS: "Load Hierarchy Labels",
    } as const;

    datePickerStore: DatePickerComponentStore;
    readonly rbcFilterStore: RBCFilterStore;
    readonly outlineFilterStore: OutlineFilterStore;
    readonly wordsAndPhrasesStore: WordsPhrasesStore;
    readonly hierarchyService: HierarchyService;

    readonly journeyStageOptions: PatientJourneyStageOption[] = [
        {
            label: "Before Treatment",
            value: PatientJourneyStage.BeforeTreatment,
        },
        {
            label: "On Treatment",
            value: PatientJourneyStage.OnTreatment,
        },
        {
            label: "After Treatment",
            value: PatientJourneyStage.AfterTreatment,
        },
        {
            label: "Restarting Treatment",
            value: PatientJourneyStage.RestartingTreatment,
        },
        {
            label: "Treatment Disruption",
            value: PatientJourneyStage.TreatmentDisruption,
        },
        {
            label: "No Clear Journey Stage",
            value: PatientJourneyStage.NoClearJourneyStage,
        },
    ];

    readonly languageOptions = [
        {
            label: "Non-English",
            value: Language.NonEnglish,
            id: undefined,
        },
        {
            label: "English",
            value: Language.English,
            id: 1033,
        },
    ];

    @observable startDate?: moment.Moment | undefined;
    @observable endDate?: moment.Moment | undefined;
    @observable dateReferenceOption: DateReferenceOption;
    @computed get dateReference() {
        return this.dateReferenceOption;
    }
    @observable dateRangeLabel?: DateRangeLabel | undefined;

    @observable minCallDuration?: number = 0;
    /** @see ApplicationFilters.maxCallDuration */
    @observable maxCallDuration?: number = 0;
    @observable minMessageCount?: number = 0;
    @observable maxMessageCount?: number = 0;
    @observable embeddedTranscriptionQuery?: string;
    @observable embeddedTranscriptionQueryThreshold: number = 0.4;
    @computed get callDuration() {
        // The maximum is exclusive but shows as the number it stops short of, which is the same
        // number the bucket it describes is labelled with, so "2–5 min" reads as 2 to 5. The
        // difference is a single millisecond value, not worth a distinct rendering.
        return [this.minCallDuration ?? 0, this.maxCallDuration ?? 0] as [
            number,
            number,
        ];
    }
    @computed get messageCount() {
        return [this.minMessageCount ?? 0, this.maxMessageCount ?? 0] as [
            number,
            number,
        ];
    }

    @observable evaluationTypes: EvaluationType[] = [];

    @observable hierarchyIds: string[] = [];
    @observable hierarchyLabels: Record<string, string> = {};
    @observable isHierarchyLabelsLoading: boolean = false;
    @observable selectedCategories: string[] = [];
    @observable selectedSubcategories: string[] = [];

    @observable agentIds: string[] = [];

    @observable agentManagerIds: string[] = [];

    @observable mediaTypes: MediaType[] = [];
    @observable callDirections: CallDirection[] = [];
    @observable clientCallId?: string;

    @observable meta1?: string;
    @observable meta2?: string;
    @observable meta3?: string;
    @observable meta4?: string;
    @observable meta5?: string;

    @observable beginningSentiment?: Sentiment;
    @observable endingSentiment?: Sentiment;

    @observable eddyEffectStatus?: boolean;

    @observable safetyEvent?: SafetyEvent;
    @observable hipaaCompliance?: HipaaCompliance;

    @observable contactTypes: string[] = [];
    @observable patientJourneyStages: PatientJourneyStage[] = [];
    @observable languages: LanguageFilterObject[] = [];

    @observable topics: SimpleTopicCluster[] = [];
    @observable themes: SimpleTheme[] = [];
    @observable primaryTopics: PrimaryTopic[] = [];
    @observable drivers: PrimaryDriver[] = [];
    @observable containsTopics: boolean = true;
    @observable containsThemes: boolean = true;
    @observable containsPrimaryTopics: boolean = true;
    @observable containsDrivers: boolean = true;
    @computed get containsTopicsString() {
        if (this.containsTopics) return "is one of";
        else return "is not one of";
    }
    @computed get containsThemesString() {
        if (this.containsThemes) return "is one of";
        else return "is not one of";
    }
    @computed get containsPrimaryTopicsString() {
        if (this.containsPrimaryTopics) return "is one of";
        else return "is not one of";
    }
    @computed get containsDriversString() {
        if (this.containsDrivers) return "is one of";
        else return "is not one of";
    }

    @observable modelResults?: SlimLLMClassifierResponse[] | undefined;
    @observable moduleScores?: ModuleScore[] | undefined;
    @observable questionResults?: QuestionResultFilter[] | undefined;

    @observable extendedMetadata?: Record<string, string>;

    @observable wordsAndPhrasesSearchString?: string;

    @computed get wordsAndPhrasesItems() {
        return this.wordsAndPhrasesStore.wordsPhrasesItems;
    }

    @computed get wordsAndPhrasesIsInputStandard() {
        return this.wordsAndPhrasesStore.isInputStandard;
    }

    @computed get rbcFilterItems() {
        return this.rbcFilterStore.RBCFilterItems;
    }

    @computed get outlineSegmentFilterItems() {
        return this.outlineFilterStore.outlineFilterItems;
    }

    @observable dataLabels: DataLabel[] = [];
    @observable containsDataLabels?: boolean;
    @observable containsResearchLabels?: boolean;
    @computed get containsDataLabelsString() {
        if (this.containsDataLabels) return "is labeled";
        return "is not labeled";
    }

    /**
     * Object for storing filter state associated to a key
     */
    @observable savedFilterStates: Partial<Record<string, ApplicationFilters>> = {};

    @observable favoriteConversations?: boolean | undefined;
    @observable extendedMetadataGrouped?: Record<string, ExtendedMetadataGrouped> | undefined;
    @observable hasError: boolean;

    constructor(rootStore: IRootStore, uniqueStoreName?: string) {
        super(uniqueStoreName ?? "Application Filters Instance Store");
        makeObservable(this);

        this.initializeDatePickerStore();
        this.copyFromDatePickerStore();
        this.rbcFilterStore = new RBCFilterStore(rootStore);
        this.outlineFilterStore = new OutlineFilterStore(rootStore);
        this.wordsAndPhrasesStore = new WordsPhrasesStore(rootStore);
        this.hierarchyService = new HierarchyService();
        this.wordsAndPhrasesStore.onSearchQueryChange = (searchPhrase) =>
            this.setWordsAndPhrasesSearchString(searchPhrase);
    }

    protected onFilterChange(): void {}

    private getCurrentWordsAndPhrasesSearchString(): string | undefined {
        const searchString = this.wordsAndPhrasesStore.isInputStandard
            ? this.wordsAndPhrasesStore.getCurrentSearchPhraseString()
            : this.wordsAndPhrasesStore.wordsPhrasesString;
        const normalizedSearch = searchString?.trim();

        if (normalizedSearch && normalizedSearch.length > 0) {
            return normalizedSearch;
        }

        const fallbackSearch = this.wordsAndPhrasesSearchString?.trim();
        return fallbackSearch && fallbackSearch.length > 0
            ? fallbackSearch
            : undefined;
    }

    @action
    private resetWordsAndPhrasesState(isInputStandard: boolean = true) {
        this.setWordsAndPhrasesItems(initWordsPhrasesItems);
        this.wordsAndPhrasesStore.setWordsPhrasesString("");
        this.setWordsAndPhrasesSearchString(undefined);
        this.setWordsAndPhrasesIsInputStandard(isInputStandard);
    }

    private resolveDateReferenceOption(
        filters: ApplicationFilters & {
            dateReference?: DateReferenceOption;
        },
    ) {
        return filters.dateReferenceOption ?? filters.dateReference;
    }

    private initializeDatePickerStore() {
        this.datePickerStore = new DatePickerComponentStore();
        this.datePickerStore.setBeginDate(
            moment().subtract(7, "days").startOf("day"),
        );
        this.datePickerStore.setEndDate(moment());
        this.datePickerStore.setReferenceOption(
            DateReferenceOption.InteractionDate,
        );
    }

    @action
    private copyFromDatePickerStore() {
        this.startDate = this.datePickerStore.beginDate;
        this.endDate = this.datePickerStore.endDate;
        this.dateReferenceOption = this.datePickerStore.referenceOption;
        this.dateRangeLabel = this.datePickerStore.dateRangePresetLabel;
    }

    @action
    public removeDateRange() {
        this.startDate = undefined;
        this.endDate = undefined;
        // A retained preset would restore the dates when the saved filter is loaded.
        this.setDateRangeLabel("");
        // Otherwise the date picker keeps displaying the just-removed range.
        this.datePickerStore?.reset();
    }

    @action
    setStartDate(startDate?: string | moment.Moment) {
        const date = moment(startDate);
        this.startDate = date;
        if (startDate) this.datePickerStore.setBeginDate(date);
        this.onFilterChange();
    }

    @action
    setEndDate(endDate?: string | moment.Moment) {
        const date = moment(endDate);
        this.endDate = date;
        if (endDate) this.datePickerStore.setEndDate(date);
        this.onFilterChange();
    }

    @action
    setDatePair(
        startDate?: string | moment.Moment,
        endDate?: string | moment.Moment,
    ) {
        this.setStartDate(startDate);
        this.setEndDate(endDate);
        this.onFilterChange();
    }

    /**
     * Sets the date picker stores reference type.
     * @default DateReferenceOption.InteractionDate
     * @param dateReferenceOption
     */
    @action
    setDateReferenceOption(dateReferenceOption?: DateReferenceOption) {
        dateReferenceOption ??= DateReferenceOption.InteractionDate;
        this.dateReferenceOption = dateReferenceOption;
        if (this.datePickerStore)
            this.datePickerStore.setReferenceOption(dateReferenceOption);
        this.onFilterChange();
    }

    @action
    setDateRangeLabel(dateRangeLabel?: DateRangeLabel) {
        dateRangeLabel ??= "";
        this.dateRangeLabel = dateRangeLabel;
        if (this.datePickerStore)
            this.datePickerStore.setDateRangePreset(dateRangeLabel);
        if (dateRangeLabel !== "") {
            const newDateRange = getDateFromDateRangeLabel(dateRangeLabel);

            this.setStartDate(newDateRange[0].startOf("d"));
            this.setEndDate(newDateRange[1].endOf("d"));
        }
        this.onFilterChange();
    }

    @action
    setMinCallDuration(duration?: number) {
        this.minCallDuration = duration;
        this.onFilterChange();
    }

    @action
    setMaxCallDuration(duration?: number) {
        this.maxCallDuration = duration;
        this.onFilterChange();
    }

    @action
    setCallDuration(duration?: [number, number] | number[]) {
        this.setMinCallDuration(duration?.[0]);
        this.setMaxCallDuration(duration?.[1]);
        this.onFilterChange();
    }

    @action
    setMinMessageCount(count?: number) {
        this.minMessageCount = count;
        this.onFilterChange();
    }

    @action
    setMaxMessageCount(count?: number) {
        this.maxMessageCount = count;
        this.onFilterChange();
    }

    @action
    setMessageCount(messageCount?: [number, number] | number[]) {
        this.setMinMessageCount(messageCount?.[0]);
        this.setMaxMessageCount(messageCount?.[1]);
        this.onFilterChange();
    }

    @action
    setEmbeddedTranscriptionQuery(query?: string) {
        this.embeddedTranscriptionQuery = query;
        this.onFilterChange();
    }

    @action
    setEmbeddedTranscriptionQueryThreshold(threshold: number) {
        this.embeddedTranscriptionQueryThreshold = threshold;
        this.onFilterChange();
    }

    @action
    setHierarchyIds(hierarchyIds: string[]) {
        this.hierarchyIds = hierarchyIds;
        this.hierarchyLabels = Object.fromEntries(
            hierarchyIds
                .filter((id) => !!this.hierarchyLabels[id])
                .map((id) => [id, this.hierarchyLabels[id]]),
        );
        if (!hierarchyIds.length) {
            this.selectedCategories = [];
            this.selectedSubcategories = [];
            this.hierarchyLabels = {};
        }
        this.onFilterChange();
    }

    @action
    setHierarchyLabels(labels: Record<string, string>) {
        this.hierarchyLabels = labels;
    }

    public async hydrateHierarchyLabels(hierarchyIds: string[] = this.hierarchyIds) {
        const missingHierarchyIds = Array.from(
            new Set(
                hierarchyIds.filter(
                    (id) => !!id && !this.hierarchyLabels[id],
                ),
            ),
        ).sort((left, right) => left.localeCompare(right));

        if (missingHierarchyIds.length === 0) return;

        this.isHierarchyLabelsLoading = true;

        const result = await this.setupAsyncTask(
            `${ApplicationFiltersInstanceStore.Tasks.LOAD_HIERARCHY_LABELS}:${missingHierarchyIds.join(",")}`,
            () => this.hierarchyService.getHierarchyLabelsByIds(missingHierarchyIds),
        );

        if (!Array.isArray(result)) {
            runInAction(() => {
                this.isHierarchyLabelsLoading = false;
            });
            return;
        }

        const nextLabels = { ...this.hierarchyLabels };
        let didChange = false;
        for (const hierarchy of result) {
            if (!hierarchy?.id || !hierarchy?.name) continue;
            if (nextLabels[hierarchy.id] === hierarchy.name) continue;
            nextLabels[hierarchy.id] = hierarchy.name;
            didChange = true;
        }

        runInAction(() => {
            this.isHierarchyLabelsLoading = false;
            if (didChange) this.setHierarchyLabels(nextLabels);
        });
    }

    @action
    removeHierarchyId(hierarchyId: string) {
        this.hierarchyIds = this.hierarchyIds.filter((id) => id !== hierarchyId);

        const nextLabels = { ...this.hierarchyLabels };
        delete nextLabels[hierarchyId];
        this.hierarchyLabels = nextLabels;

        // Category/subcategory filters depend on the selected hierarchy set.
        this.selectedCategories = [];
        this.selectedSubcategories = [];
        this.onFilterChange();
    }

    @action
    setSelectedCategories(categories: string[], subcategories: string[]) {
        this.selectedCategories = categories;
        this.selectedSubcategories = subcategories;
    }

    @action
    setAgentIds(agents?: string[]) {
        this.agentIds = agents ?? [];
        this.onFilterChange();
    }

    @action
    setAgentManagerIds(agentManagers?: string[]) {
        this.agentManagerIds = agentManagers ?? [];
        this.onFilterChange();
    }

    @action
    setMediaTypes(mediaTypes: MediaType[]) {
        this.mediaTypes = mediaTypes;
        this.onFilterChange();
    }

    @action
    toggleMediaType(mediaType: MediaType) {
        if (this.mediaTypes.includes(mediaType))
            this.mediaTypes = this.mediaTypes.filter(
                (type) => type !== mediaType,
            );
        else this.mediaTypes.push(mediaType);
        this.onFilterChange();
    }

    @action
    setCallDirections(callDirections: CallDirection[]) {
        this.callDirections = callDirections;
        this.onFilterChange();
    }

    @action
    toggleCallDirection(callDirection: CallDirection) {
        if (this.callDirections.includes(callDirection))
            this.callDirections = this.callDirections.filter(
                (direction) => direction !== callDirection,
            );
        else this.callDirections.push(callDirection);
        this.onFilterChange();
    }

    @action
    setClientCallId(clientCallId?: string) {
        this.clientCallId = clientCallId;
        this.onFilterChange();
    }

    @action
    setMeta1(meta1?: string) {
        this.meta1 = meta1;
        this.onFilterChange();
    }

    @action
    setMeta2(meta2?: string) {
        this.meta2 = meta2;
        this.onFilterChange();
    }
    @action
    setMeta3(meta3?: string) {
        this.meta3 = meta3;
        this.onFilterChange();
    }

    @action
    setMeta4(meta4?: string) {
        this.meta4 = meta4;
        this.onFilterChange();
    }

    @action
    setMeta5(meta5?: string) {
        this.meta5 = meta5;
        this.onFilterChange();
    }

    @action
    setExtendedMetadata(extendedMetadata?: Record<string, string>) {
        this.extendedMetadata = extendedMetadata;
        this.onFilterChange();
    }

    @action
    removeExtendedMetadataPair(key: string) {
        if (!this.extendedMetadata) return;
        if (!Object.hasOwn(this.extendedMetadata, key)) return;
        delete this.extendedMetadata[key];
    }

    @action
    setExtendedMetadataPair(key: string, value: string) {
        if (!this.extendedMetadata) this.setExtendedMetadata({});
        this.extendedMetadata![key] = value;
        this.onFilterChange();
    }

    @action
    setBeginningSentiment(sentiment?: Sentiment) {
        this.beginningSentiment = sentiment;
        this.onFilterChange();
    }

    @action
    setEndingSentiment(sentiment?: Sentiment) {
        this.endingSentiment = sentiment;
        this.onFilterChange();
    }

    @action
    setEddyEffectStatus(status?: boolean) {
        this.eddyEffectStatus = status;
        this.onFilterChange();
    }

    @action
    setSafetyEvent(safetyEventUpdate: Partial<SafetyEvent> | undefined) {

        // set defaults if safety event is undefined
        if (this.safetyEvent === undefined) {
            this.safetyEvent = {
                identification: null,
                typeOperator: SafetyEventTypeOperator.Or,
                types: [],
                acknowledgments: [],
            }
        }

        if (!safetyEventUpdate) {
            this.safetyEvent = undefined;
        } else {

            const updatedSafetyEvent = {
                ...this.safetyEvent,
                ...safetyEventUpdate,
            }

            this.safetyEvent = updatedSafetyEvent;
        }
        this.onFilterChange();
    }

    @action
    setHipaaCompliance(hipaaCompliance?: HipaaCompliance) {
        this.hipaaCompliance = hipaaCompliance;
        this.onFilterChange();
    }

    @action
    setContactTypes(contactTypes: string[] | ContactTypeOption[]) {
        if (contactTypes.length === 0) this.contactTypes = [];
        else {
            const singleContactType = contactTypes[0];
            if (typeof singleContactType === "string")
                this.contactTypes = contactTypes as string[];
            else
                this.contactTypes = (contactTypes as ContactTypeOption[]).map(
                    (i) => i.value,
                );
        }

        this.onFilterChange();
    }

    @action
    setPatientJourneyStages(patientJourneyStages: PatientJourneyStageOption[]) {
        if (patientJourneyStages.length === 0) this.patientJourneyStages = [];
        else {
            this.patientJourneyStages = patientJourneyStages.map(
                (i) => i.value,
            );
        }

        this.onFilterChange();
    }

    @action
    setLanguages(languages: LanguageFilterObject[]) {
        this.languages = languages;

        this.onFilterChange();
    }

    @action
    setTopics(topics: SimpleTopicCluster[]) {
        this.topics = topics;
        this.onFilterChange();
    }

    @action
    setThemes(themes: SimpleTheme[]) {
        this.themes = themes;
        this.onFilterChange();
    }

    @action
    setPrimaryTopics(topics: PrimaryTopic[]) {
        this.primaryTopics = topics;
        this.onFilterChange();
    }

    @action
    setDrivers(drivers: PrimaryDriver[]) {
        this.drivers = drivers;
        this.onFilterChange();
    }

    @action
    setContainsTopics(contains: boolean) {
        this.containsTopics = contains;
        this.onFilterChange();
    }

    @action
    setContainsThemes(contains: boolean) {
        this.containsThemes = contains;
        this.onFilterChange();
    }

    @action
    setContainsPrimaryTopics(contains: boolean) {
        this.containsPrimaryTopics = contains;
        this.onFilterChange();
    }

    @action
    setContainsDrivers(contains: boolean) {
        this.containsDrivers = contains;
        this.onFilterChange();
    }

    @action
    setModelResults(modelResults: SlimLLMClassifierResponse[]) {
        this.modelResults = modelResults;
        this.onFilterChange();
    }

    @action
    removeResultFromModel(model: SlimLLMClassifierResponse, resultToRemove: string) {
        const newResults = [...model.results.filter(i => i !== resultToRemove)];

        if(!this.modelResults) return

        if (
            !newResults.length 
        ) {
            // No more results selected for model, remove model from selection
            this.setModelResults(this.modelResults.filter(i => i.id !== model.id))
        } else {
            // update results for selected model
            this.setModelResults(this.modelResults.map(i=>{
                if(i.id === model.id){
                    i.results = newResults
                }
                return i
            }))
        }
    }

    @action
    setModuleScores(moduleScores: ModuleScore[]) {
        this.moduleScores = moduleScores;
        this.onFilterChange();
    }

    @action
    setQuestionResults(questionResults: QuestionResultFilter[]) {
        this.questionResults = questionResults;
        this.onFilterChange();
    }

    @action
    setWordsAndPhrasesSearchString(search?: string) {
        const normalizedSearch = search?.trim();
        this.wordsAndPhrasesSearchString =
            normalizedSearch && normalizedSearch.length > 0
                ? normalizedSearch
                : undefined;
        this.onFilterChange();
    }

    @action
    setWordsAndPhrasesItems(wordsAndPhrasesItems: IWordsPhrasesModel[]) {
        this.wordsAndPhrasesStore.setWordsPhrasesItems(wordsAndPhrasesItems);
        this.onFilterChange();
    }

    @action
    setWordsAndPhrasesIsInputStandard(isInputStandard?: boolean) {
        if (this.wordsAndPhrasesStore.isInputStandard !== isInputStandard)
            this.wordsAndPhrasesStore.toggleStandardInput();
        this.onFilterChange();
    }

    @action
    setRBCFilterItems(rbcFilterItems: IRBCFilter[]) {
        this.rbcFilterStore.setRBCFilterItems(rbcFilterItems);
        this.onFilterChange();
    }

    @action
    setOutlineSegmentFilterItems(outlineSegmentFilterItems: IOutlineFilter[]) {
        this.outlineFilterStore.setOutlineFilterItems(outlineSegmentFilterItems);
        this.onFilterChange();
    }

    @action
    setFavoriteConversations(favoriteConversations?: boolean) {
        this.favoriteConversations = favoriteConversations;
        this.onFilterChange();
    }

    @action
    setContainsConversationLabels(containsDataLabels?: boolean) {
        this.containsDataLabels = containsDataLabels;
        this.onFilterChange();
    }

    @action
    setConversationLabels(dataLabels: DataLabel[]) {
        this.dataLabels = dataLabels;
        this.onFilterChange();
    }

    /**
     * Facade for calling rbcFilterStore.removeClassifierById but also ensures
     * that this.onFilterChange is invoked afterwards.
     * @param classifierId
     */
    @action
    removeClassifierById(classifierId: string) {
        const result = this.rbcFilterStore.removeClassifierById(classifierId);
        this.onFilterChange();
        return result;
    }

    /**
     * Facade for calling outlineFilterStore.removeSegmentById but also ensures
     * that this.onFilterChange is invoked afterwards.
     * @param segmentId
     */
    @action
    removeOutlineSegmentById(segmentId: string) {
        const result = this.outlineFilterStore.removeSegmentById(segmentId);
        this.onFilterChange();
        return result;
    }

    @action
    setEvaluationTypes(evaluationTypes: EvaluationType[]) {
        this.evaluationTypes = evaluationTypes;
        this.onFilterChange();
    }

    /**
     * Directly indexes this value with `key` setting it to `value`
     * @param key an application filter key
     * @param value
     */
    @action
    setByKey(
        key:
            | keyof ApplicationFilters
            | "callDuration"
            | "messageCount"
            | `extendedMetadata.${string}`,
        value: unknown,
    ) {
        if (key === "callDuration") {
            this.setCallDuration(
                Array.isArray(value) ? (value as number[]) : undefined,
            );
            return;
        }
        if (key === "messageCount") {
            this.setMessageCount(
                Array.isArray(value) ? (value as number[]) : undefined,
            );
            return;
        }
        _.set(this, key, value);
        this.onFilterChange();
    }

    @action
    resetByKey(
        key:
            | keyof ApplicationFilters
            | "callDuration"
            | "messageCount"
            | `extendedMetadata.${string}`,
        resetValue: unknown = undefined,
    ) {
        if (key.startsWith("extendedMetadata.")) {
            const metadataKey = key.replace("extendedMetadata.", "");

            if (metadataKey.length > 0) {
                if (this.extendedMetadata) {
                    const nextExtendedMetadata = {
                        ...this.extendedMetadata,
                    };
                    delete nextExtendedMetadata[metadataKey];
                    this.extendedMetadata =
                        Object.keys(nextExtendedMetadata).length > 0
                            ? nextExtendedMetadata
                            : undefined;
                }

                if (this.extendedMetadataGrouped) {
                    const nextGrouped = {
                        ...this.extendedMetadataGrouped,
                    };
                    delete nextGrouped[metadataKey];
                    this.extendedMetadataGrouped =
                        Object.keys(nextGrouped).length > 0
                            ? nextGrouped
                            : undefined;
                }
            }
        } else if (key === "rbcFilterItems")
            this.setRBCFilterItems(initRBCFilterItems);
        else if (key === "outlineSegmentFilterItems")
            this.setOutlineSegmentFilterItems(initOutlineFilterItems);
        else if (key === "wordsAndPhrasesSearchString")
            this.resetWordsAndPhrasesState(true);
        else if (key === "callDuration")
            this.setCallDuration([0, 0]);
        else if (key === "messageCount")
            this.setMessageCount([0, 0]);
        else if (key === "dateReferenceOption")
            this.setDateReferenceOption(DateReferenceOption.InteractionDate);
        else if (key === "hierarchyIds") {
            this.setHierarchyIds([]);
            this.setSelectedCategories([], []);
        }
        else if (Array.isArray(_.get(this, key))) _.set(this, key, []);
        else _.set(this, key, resetValue);
        this.onFilterChange();
    }

    /**
     * Given an evaluation type controls whether its present in the evaluation
     * types array. Takes into account that not evaluated cannot be selected
     * alongside other evaluated options.
     * @param evaluationType
     * @returns
     */
    @action
    toggleEvaluationType(evaluationType: EvaluationType) {
        if (evaluationType === EvaluationType.NotEvaluated) {
            if (this.evaluationTypes.includes(EvaluationType.NotEvaluated))
                this.evaluationTypes = [];
            else this.evaluationTypes = [EvaluationType.NotEvaluated];
            return;
        }

        if (
            evaluationType === EvaluationType.HumanEvaluated ||
            evaluationType === EvaluationType.AIEvaluated
        ) {
            if (this.evaluationTypes.includes(EvaluationType.NotEvaluated))
                this.evaluationTypes.splice(
                    this.evaluationTypes.indexOf(EvaluationType.NotEvaluated),
                    1,
                );

            const index = this.evaluationTypes.indexOf(evaluationType);
            if (index >= 0) this.evaluationTypes.splice(index, 1);
            else this.evaluationTypes.push(evaluationType);
        }
        this.onFilterChange();
    }

    @action
    toggleFavoriteConversationsFilter() {
        this.favoriteConversations = this.favoriteConversations
            ? undefined
            : true;
    }

    @computed get hasChangedSinceLastUpdate() {
        return this.hasFilterStateChangedSince("Update");
    }

    /**
     * Given a key to a saved filter state, return a boolean
     * indicating whether the state at the given key is different
     * than the current state
     * @param referenceStateKey
     * @returns
     */
    public hasFilterStateChangedSince(referenceStateKey: string) {
        const referenceState = this.savedFilterStates[referenceStateKey];
        if (!referenceState) return false;

        return this.areFiltersDifferent(_.cloneDeep(referenceState));
    }

    @action
    public saveCurrentFilterState(key: string) {
        this.savedFilterStates[key] = _.cloneDeep(this.toFilterObject());
    }

    @action
    public saveExternalFilterState(key: string, filters: ApplicationFilters) {
        this.savedFilterStates[key] = _.cloneDeep(filters);
    }

    @action
    public setHasError(hasError: boolean) {
        this.hasError = hasError;
    }

    public async validateWordsAndPhrases() {
        const currentSearchString = this.getCurrentWordsAndPhrasesSearchString();
        if (
            ApplicationFiltersInstanceStore.isStringUndefinedOrEmpty(
                currentSearchString,
            )
        )
            return true;

        return this.setupAsyncTask(
            ApplicationFiltersInstanceStore.Tasks.VALIDATE_WORDS_AND_PHRASES,
            async () => {
                this.wordsAndPhrasesStore.setWordsPhrasesString(
                    currentSearchString ?? "",
                );
                this.setWordsAndPhrasesSearchString(currentSearchString);
                await this.wordsAndPhrasesStore.validateWordsPhrasesAdvString();
                return this.wordsAndPhrasesStore.isValidWordsPhrasesString;
            },
        );
    }

    @action
    public setExtendedMetadataGrouped(extendedMetadataGrouped?: Record<string, ExtendedMetadataGrouped>) {
        this.extendedMetadataGrouped = sanitizeExtendedMetadataGrouped(
            extendedMetadataGrouped,
        );
        this.onFilterChange();
    }

    @action
    public updateExtendedMetadataGrouped(key: string, value: ExtendedMetadataGrouped) {
        const normalizedValues = normalizeExtendedMetadataGroupedValues(
            value.values,
        );
        const nextGrouped = { ...(this.extendedMetadataGrouped ?? {}) };
        const nextExtendedMetadata = { ...(this.extendedMetadata ?? {}) };

        if (normalizedValues.length > 0) {
            nextGrouped[key] = {
                contains: value.contains,
                values: normalizedValues,
            };
        } else {
            delete nextGrouped[key];
        }

        if (value.contains && normalizedValues.length > 0) {
            nextExtendedMetadata[key] = normalizedValues.join(", ");
        } else {
            delete nextExtendedMetadata[key];
        }

        this.extendedMetadataGrouped =
            Object.keys(nextGrouped).length > 0 ? nextGrouped : undefined;
        this.extendedMetadata =
            Object.keys(nextExtendedMetadata).length > 0
                ? nextExtendedMetadata
                : undefined;
        this.onFilterChange();
    }

    @action
    public resetFilters() {
        this.initializeDatePickerStore();
        this.copyFromDatePickerStore();
        this.setCallDuration([0, 0]);
        this.setMessageCount([0, 0]);
        this.setHierarchyIds([]);
        this.setSelectedCategories([], []);
        this.setEvaluationTypes([]);
        this.setMediaTypes([]);
        this.setCallDirections([]);
        this.setClientCallId(undefined);
        this.setEddyEffectStatus(undefined);
        this.setHipaaCompliance(undefined);
        this.setBeginningSentiment(undefined);
        this.setEndingSentiment(undefined);
        this.setSafetyEvent(undefined);
        this.setAgentIds([]);
        this.setAgentManagerIds([]);
        this.setContainsTopics(true);
        this.setTopics([]);
        this.setContainsThemes(true);
        this.setThemes([]);
        this.setContainsPrimaryTopics(true);
        this.setPrimaryTopics([]);
        this.setContainsDrivers(true);
        this.setDrivers([]);
        this.setRBCFilterItems(initRBCFilterItems);
        this.setOutlineSegmentFilterItems(initOutlineFilterItems);
        this.resetWordsAndPhrasesState(true);
        this.setEmbeddedTranscriptionQuery(undefined);
        this.setEmbeddedTranscriptionQueryThreshold(0.4);
        this.setMeta1(undefined);
        this.setMeta2(undefined);
        this.setMeta3(undefined);
        this.setMeta4(undefined);
        this.setMeta5(undefined);
        this.setExtendedMetadata(undefined);
        this.setExtendedMetadataGrouped(undefined);
        this.setContactTypes([]);
        this.setPatientJourneyStages([]);
        this.setFavoriteConversations(undefined);
        this.setContainsConversationLabels(false);
        this.setConversationLabels([]);
        this.setModelResults([]);
        this.setModuleScores([]);
        this.setQuestionResults([]);
        this.setLanguages([]);
    }

    @action
    applyExternalFilters(
        filters: ApplicationFilters,
        hydrateHierarchyLabels: boolean = true,
    ) {
        const normalizedDateReferenceOption = this.resolveDateReferenceOption(
            filters as ApplicationFilters & {
                dateReference?: DateReferenceOption;
            },
        );

        if (!filters.startDate && !filters.endDate) {
            this.removeDateRange();
        } else {
            this.setStartDate(filters.startDate);
            this.setEndDate(filters.endDate);
        }
        this.setDateReferenceOption(normalizedDateReferenceOption);
        this.setDateRangeLabel(filters.dateRangeLabel ?? "");
        this.setMinCallDuration(filters.minCallDuration);
        this.setMaxCallDuration(filters.maxCallDuration);
        this.setMinMessageCount(filters.minMessageCount);
        this.setMaxMessageCount(filters.maxMessageCount);
        this.setEvaluationTypes(filters.evaluationTypes ?? []);
        this.setHierarchyIds(filters.hierarchyIds ?? []);
        if (hydrateHierarchyLabels) {
            void this.hydrateHierarchyLabels(filters.hierarchyIds ?? []);
        }
        this.setSelectedCategories(
            filters.selectedCategories ?? [],
            filters.selectedSubcategories ?? [],
        );
        this.setMediaTypes(filters.mediaTypes ?? []);
        this.setCallDirections(filters.callDirections ?? []);
        this.setClientCallId(filters.clientCallId);
        this.setMeta1(filters.meta1);
        this.setMeta2(filters.meta2);
        this.setMeta3(filters.meta3);
        this.setMeta4(filters.meta4);
        this.setMeta5(filters.meta5);
        this.setExtendedMetadata(filters.extendedMetadata);
        this.setExtendedMetadataGrouped(filters.extendedMetadataGrouped);
        this.setBeginningSentiment(filters.beginningSentiment);
        this.setEndingSentiment(filters.endingSentiment);
        this.setEddyEffectStatus(filters.eddyEffectStatus);
        this.setSafetyEvent(filters.safetyEvent);
        this.setHipaaCompliance(filters.hipaaCompliance);
        this.setTopics(filters.topics ?? []);
        this.setThemes(filters.themes ?? []);
        this.setPrimaryTopics(filters.primaryTopics ?? []);
        this.setDrivers(filters.drivers ?? []);
        this.setContainsTopics(filters.containsTopics ?? true);
        this.setContainsThemes(filters.containsThemes ?? true);
        this.setContainsPrimaryTopics(filters.containsPrimaryTopics ?? true);
        this.setContainsDrivers(filters.containsDrivers ?? true);
        this.setModelResults(filters.modelResults ?? []);
        this.setModuleScores(filters.moduleScores ?? []);
        this.setQuestionResults(filters.questionResults ?? []);

        const nextWordsAndPhrasesIsInputStandard =
            filters.wordsAndPhrasesIsInputStandard ?? true;
        this.resetWordsAndPhrasesState(nextWordsAndPhrasesIsInputStandard);
        // only set the wordsPhrasesItems if they exist, otherwise it will trigger an update to the wordsPhrases query string and clear it
        if (
            filters.wordsAndPhrasesItems &&
            filters.wordsAndPhrasesItems.length &&
            filters.wordsAndPhrasesItems[0].searchPhrase !== ""
        ) {
            this.setWordsAndPhrasesItems(filters.wordsAndPhrasesItems);
        }
        const wordsAndPhrasesSearchString =
            filters.wordsAndPhrasesSearchString?.trim();
        const nextWordsAndPhrasesSearchString =
            wordsAndPhrasesSearchString && wordsAndPhrasesSearchString.length > 0
                ? wordsAndPhrasesSearchString
                : nextWordsAndPhrasesIsInputStandard === false
                  ? ""
                  : this.wordsAndPhrasesStore.getCurrentSearchPhraseString();
        this.wordsAndPhrasesStore.setWordsPhrasesString(
            nextWordsAndPhrasesSearchString,
        );
        this.setWordsAndPhrasesSearchString(nextWordsAndPhrasesSearchString);
        this.setEmbeddedTranscriptionQuery(filters.embeddedTranscriptionQuery);
        this.setEmbeddedTranscriptionQueryThreshold(
            filters.embeddedTranscriptionQueryThreshold ?? 0.4,
        );
        this.setRBCFilterItems(filters.rbcFilterItems ?? initRBCFilterItems);
        this.setOutlineSegmentFilterItems(
            filters.outlineSegmentFilterItems ?? initOutlineFilterItems,
        );
        this.setContactTypes(
            (filters.contactTypes ?? []).map((i) => ({ label: i, value: i })),
        );
        this.setPatientJourneyStages(
            (filters.patientJourneyStages ?? []).map((i) => ({
                label: this.journeyStageOptions.find(
                    (stage) => stage.value === i,
                )?.label as string,
                value: i,
            })),
        );
        this.setFavoriteConversations(filters.favoriteConversations);
        this.setContainsConversationLabels(filters.containsDataLabels ?? false);
        this.setConversationLabels(filters.dataLabels ?? []);
        this.setAgentIds(filters.agentIds ?? []);
        this.setAgentManagerIds(filters.agentManagerIds ?? []);
        this.setLanguages(filters.languages ?? []);
    }

    /**
     * @returns A non-observed object with all filterables
     */
    toFilterObject(): ApplicationFilters {
        const wordsAndPhrasesSearchString =
            this.getCurrentWordsAndPhrasesSearchString();

        return toJS({
            startDate: this.startDate,
            endDate: this.endDate,
            dateReference: this.dateReference,
            dateReferenceOption: this.dateReferenceOption,
            dateRangeLabel: this.dateRangeLabel,
            minCallDuration: this.minCallDuration,
            maxCallDuration: this.maxCallDuration,
            minMessageCount: this.minMessageCount,
            maxMessageCount: this.maxMessageCount,
            evaluationTypes: this.evaluationTypes,
            hierarchyIds: this.hierarchyIds,
            selectedCategories: this.selectedCategories,
            selectedSubcategories: this.selectedSubcategories,
            agentIds: this.agentIds,
            agentManagerIds: this.agentManagerIds,
            mediaTypes: this.mediaTypes,
            callDirections: this.callDirections,
            clientCallId: this.clientCallId,
            meta1: this.meta1,
            meta2: this.meta2,
            meta3: this.meta3,
            meta4: this.meta4,
            meta5: this.meta5,
            extendedMetadata: this.extendedMetadata,
            extendedMetadataGrouped: this.extendedMetadataGrouped,
            beginningSentiment: this.beginningSentiment,
            endingSentiment: this.endingSentiment,
            eddyEffectStatus: this.eddyEffectStatus,
            safetyEvent: this.safetyEvent,
            hipaaCompliance: this.hipaaCompliance,
            topics: this.topics,
            themes: this.themes,
            primaryTopics: this.primaryTopics,
            drivers: this.drivers,
            containsTopics: this.containsTopics,
            containsThemes: this.containsThemes,
            containsPrimaryTopics: this.containsPrimaryTopics,
            containsDrivers: this.containsDrivers,
            wordsAndPhrasesSearchString: wordsAndPhrasesSearchString,
            wordsAndPhrasesItems: this.wordsAndPhrasesItems,
            wordsAndPhrasesIsInputStandard: this.wordsAndPhrasesIsInputStandard,
            rbcFilterItems: this.rbcFilterItems,
            outlineSegmentFilterItems: this.outlineSegmentFilterItems,
            contactTypes: this.contactTypes,
            patientJourneyStages: this.patientJourneyStages,
            favoriteConversations: this.favoriteConversations,
            containsDataLabels: this.containsDataLabels,
            dataLabels: this.dataLabels,
            modelResults: this.modelResults,
            moduleScores: this.moduleScores,
            questionResults: this.questionResults,
            embeddedTranscriptionQuery: this.embeddedTranscriptionQuery,
            embeddedTranscriptionQueryThreshold:
                this.embeddedTranscriptionQueryThreshold,
            languages: this.languages,
        });
    }

    /**
     * Uses toFilterObject but applies some processing to meta 1-5 and extended metadata.
     * This should still return a valid ApplicationFilters object.
     */
    toRequestObject(): ApplicationFilters {
        const filters = this.toFilterObject();

        const dates = toIsoByDateReference(
            moment(filters.startDate),
            moment(filters.endDate),
            filters.dateReferenceOption,
        );

        filters.startDate = dates.beginDate;
        filters.endDate = dates.endDate;

        filters.meta1?.replace(/'/g, "''");
        filters.meta2?.replace(/'/g, "''");
        filters.meta3?.replace(/'/g, "''");
        filters.meta4?.replace(/'/g, "''");
        filters.meta5?.replace(/'/g, "''");

        if (filters.extendedMetadata) {
            const extendedMetadata = { ...filters.extendedMetadata };
            for (const [key, value] of Object.entries(extendedMetadata)) {
                if (!value) {
                    // A filter with an empty value causes the call to fail
                    delete extendedMetadata[key];
                    continue;
                }
                extendedMetadata[key] = value.replace(/'/g, "''");
            }
            filters.extendedMetadata = extendedMetadata;
        }

        filters.rbcFilterItems =
            filters.rbcFilterItems?.filter(
                (item) => item.classifierIds.length > 0,
            ) ?? [];

        filters.outlineSegmentFilterItems =
            filters.outlineSegmentFilterItems?.filter(
                (item) => item.segments.length > 0,
            ) ?? [];

        filters.modelResults = filters.modelResults?.filter(
            (item) => item.results.length > 0,
        );

        filters.questionResults = filters.questionResults?.filter(
            (item) => item.tagIds.length > 0,
        );

        if (filters.minCallDuration === 0) filters.minCallDuration = undefined;
        if (filters.maxCallDuration === 0) filters.maxCallDuration = undefined;
        if (filters.minMessageCount === 0) filters.minMessageCount = undefined;
        if (filters.maxMessageCount === 0) filters.maxMessageCount = undefined;

        return filters;
    }

    private normalizeFiltersForComparison(filters: ApplicationFilters) {
        const normalizedFilters = filters;

        const normalizeDateValue = (
            value?: string | moment.Moment,
            granularity: moment.unitOfTime.StartOf = "day",
        ) => {
            if (!value) return value;

            const normalizedMoment = moment(value);
            if (!normalizedMoment.isValid()) return value;

            return normalizedMoment.startOf(granularity).format("YYYY-MM-DD");
        };

        normalizedFilters.startDate = normalizeDateValue(
            normalizedFilters.startDate,
        );
        normalizedFilters.endDate = normalizeDateValue(
            normalizedFilters.endDate,
        );

        return normalizedFilters;
    }

    public areFiltersDifferent(filters: ApplicationFilters) {
        const currentFilters = this.normalizeFiltersForComparison(
            this.toFilterObject(),
        );
        const comparisonFilters =
            this.normalizeFiltersForComparison(filters);

        return !_.isEqual(currentFilters, comparisonFilters);
    }

    /**
     * Compares this store (source) to candidate filters on a per-key basis.
     * Intended for object-level filtering rules and conflict detection.
     */
    public compareFiltersByKey(
        candidateFilters: ApplicationFilters | ApplicationFiltersInstanceStore,
        keys: readonly ObjectFilterKey[] = OBJECT_FILTER_KEYS,
        context?: FilterComparisonContext,
    ): FiltersComparisonResult {
        return compareObjectFiltersByKey(this, candidateFilters, keys, context);
    }

    protected static isStringUndefinedOrEmpty(value?: string) {
        return value === undefined || value === "";
    }

    protected static isUndefinedOrNull<T>(
        value?: T | null,
    ): value is undefined | null {
        return value === undefined || value === null;
    }

    get appliedFilterCount() {
        return ApplicationFiltersInstanceStore.getAppliedFilterCount(this);
    }

    public static getAppliedFilterCount(filters: ApplicationFilters) {
        let count = 0;

        if (
            (!!filters.minCallDuration && filters.minCallDuration !== 0) ||
            (!!filters.maxCallDuration && filters.maxCallDuration !== 0)
        )
            count++;
        if (
            (!!filters.minMessageCount && filters.minMessageCount !== 0) ||
            (!!filters.maxMessageCount && filters.maxMessageCount !== 0)
        )
            count++;
        if (
            !this.isUndefinedOrNull(filters.mediaTypes) &&
            filters.mediaTypes.length > 0
        )
            count++;
        if (
            !this.isUndefinedOrNull(filters.callDirections) &&
            filters.callDirections.length > 0
        )
            count++;
        if (!this.isStringUndefinedOrEmpty(filters.clientCallId)) count++;
        if (
            !this.isUndefinedOrNull(filters.hierarchyIds) &&
            filters.hierarchyIds.length > 0
        )
            count++;
        if (!this.isUndefinedOrNull(filters.eddyEffectStatus)) count++;
        if (!this.isUndefinedOrNull(filters.hipaaCompliance)) count++;
        if (!this.isUndefinedOrNull(filters.beginningSentiment)) count++;
        if (!this.isUndefinedOrNull(filters.endingSentiment)) count++;
        if (!this.isUndefinedOrNull(filters.safetyEvent) &&
            (filters.safetyEvent.identification !== null ||
                filters.safetyEvent.types.length > 0 ||
                filters.safetyEvent.acknowledgments.length > 0)) count++;
        if (
            !this.isUndefinedOrNull(filters?.agentIds) &&
            (filters?.agentIds ?? []).length > 0
        )
            count++;
        if (
            !this.isUndefinedOrNull(filters?.agentManagerIds) &&
            (filters?.agentManagerIds ?? []).length > 0
        )
            count++;
        if (
            !this.isUndefinedOrNull(filters.topics) &&
            filters.topics.length > 0
        )
            count++;
        if (
            !this.isUndefinedOrNull(filters.themes) &&
            filters.themes.length > 0
        )
            count++;
        if (
            !this.isUndefinedOrNull(filters.primaryTopics) &&
            filters.primaryTopics.length > 0
        )
            count++;
        if (
            !this.isUndefinedOrNull(filters.drivers) &&
            filters.drivers.length > 0
        )
            count++;
        if (
            !this.isUndefinedOrNull(filters.contactTypes) &&
            filters.contactTypes.length > 0
        )
            count++;
        if (
            !this.isUndefinedOrNull(filters.patientJourneyStages) &&
            filters.patientJourneyStages.length > 0
        )
            count++;
        if (
            !this.isUndefinedOrNull(filters.rbcFilterItems) &&
            filters.rbcFilterItems.length > 0 &&
            filters.rbcFilterItems.some(
                (value) => value.classifierIds.length > 0,
            )
        )
            count++;
        if (
            !this.isUndefinedOrNull(filters.outlineSegmentFilterItems) &&
            filters.outlineSegmentFilterItems.length > 0 &&
            filters.outlineSegmentFilterItems.some(
                (value) => value.segments.length > 0,
            )
        )
            count++;
        if (!this.isStringUndefinedOrEmpty(filters.wordsAndPhrasesSearchString))
            count++;
        if (
            !this.isUndefinedOrNull(filters.evaluationTypes) &&
            filters.evaluationTypes.length > 0
        )
            count++;

        if (!this.isStringUndefinedOrEmpty(filters.meta1)) count++;
        if (!this.isStringUndefinedOrEmpty(filters.meta2)) count++;
        if (!this.isStringUndefinedOrEmpty(filters.meta3)) count++;
        if (!this.isStringUndefinedOrEmpty(filters.meta4)) count++;
        if (!this.isStringUndefinedOrEmpty(filters.meta5)) count++;

        const extendedMetadataGroupedKeys = new Set(
            filters.extendedMetadataGrouped
                ? Object.keys(filters.extendedMetadataGrouped)
                : [],
        );

        if (filters.extendedMetadata)
            for (const [key, value] of Object.entries(
                filters.extendedMetadata,
            )) {
                if (extendedMetadataGroupedKeys.has(key)) continue;
                if (
                    !this.isUndefinedOrNull(value) &&
                    !this.isStringUndefinedOrEmpty(value)
                )
                    count++;
            }

        if (filters.extendedMetadataGrouped) {
            for (const data of Object.values(filters.extendedMetadataGrouped)) {
                if (data.values.length > 0) count++;
            }
        }

        if (filters.favoriteConversations) count++;
        if (filters.dataLabels && filters.dataLabels.length > 0) count++;
        if (
            !this.isUndefinedOrNull(filters.modelResults) &&
            filters.modelResults.length > 0
        )
            count++;

        if (
            !this.isUndefinedOrNull(filters.moduleScores) &&
            filters.moduleScores.length > 0
        )
            count++;

        if (
            !this.isUndefinedOrNull(filters.questionResults) &&
            filters.questionResults.length > 0
        )
            count++;

        if (!this.isStringUndefinedOrEmpty(filters.embeddedTranscriptionQuery))
            count++;

        if (
            !this.isUndefinedOrNull(filters.languages) &&
            filters.languages.length > 0
        )
            count++;

        return count;
    }
}
